/**
 * "Dit is een cadeau" in de winkelwagen (README §5.4 / §7.11).
 *
 * Aanvinken: Cadeauverpakking (€ 2,50) in de wagen + attributen cadeau/kaartje,
 * in één cart/update.js. Uitvinken: product eruit, attributen leeg. Het
 * standaard-event uit smokkelaar-cart.js rendert lade, pagina en teller.
 *
 * Het kaartje slaat zichzelf op zodra je even stopt met typen (en bij verlaten
 * van het veld) en zegt dan "Kaartje opgeslagen" (lijst C). De tekst staat ook
 * in sessionStorage: vink je uit en weer aan, of herlaad je de pagina, dan is hij
 * er nog. De textarea heeft data-skip-node-update, dus een herrender van de wagen
 * wist niet wat je typt.
 */

import { zetAantallen } from '@theme/smokkelaar-cart';

const SLEUTEL = 'smokkelaar_kaartje';
const WACHT = 800;

const bewaard = () => {
  try { return sessionStorage.getItem(SLEUTEL) || ''; } catch { return ''; }
};
const bewaar = (tekst) => {
  try { sessionStorage.setItem(SLEUTEL, tekst); } catch { /* privémodus: dan alleen het attribuut */ }
};

class SmokkelaarCadeau extends HTMLElement {
  #timer = 0;
  #opgeslagen = null;

  connectedCallback() {
    const vink = this.querySelector('[data-cadeau]');
    const kaartje = this.querySelector('[data-kaartje]');
    vink?.addEventListener('change', (e) => this.#zet(e.target.checked));
    if (!kaartje) return;

    // Uitgevinkt en leeg (bijv. na herladen): de laatst getypte tekst klaarzetten voor het volgende vinkje.
    if (!vink?.checked && !kaartje.value) kaartje.value = bewaard();
    this.#opgeslagen = vink?.checked ? kaartje.value : null;

    kaartje.addEventListener('input', () => {
      bewaar(kaartje.value);
      this.#status('');
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#slaOp(kaartje.value), WACHT);
    });
    kaartje.addEventListener('change', () => {
      clearTimeout(this.#timer);
      this.#slaOp(kaartje.value);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  async #zet(aan) {
    const id = this.dataset.variant;
    const vink = this.querySelector('[data-cadeau]');
    if (!vink) return;
    vink.disabled = true;
    clearTimeout(this.#timer);
    this.#status('');
    const kaartje = aan ? this.querySelector('[data-kaartje]')?.value || bewaard() : '';
    try {
      await zetAantallen({ [id]: aan ? 1 : 0 }, { bron: this, attributes: { cadeau: aan ? 'ja' : '', kaartje } });
      this.#opgeslagen = aan ? kaartje : null;
    } catch (err) {
      console.error('Cadeau:', err);
      vink.checked = !aan;
    } finally {
      // Meestal is het element dan al bijgewerkt door de herrender; anders weer bruikbaar.
      vink.disabled = false;
    }
  }

  // Alleen tekst: geen herrender nodig, dus buiten het event om.
  async #slaOp(tekst) {
    // Zonder vinkje hoort er geen kaartje op de bestelling; de tekst staat wel in sessionStorage.
    if (!this.querySelector('[data-cadeau]')?.checked || tekst === this.#opgeslagen) return;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { kaartje: tekst } }),
      });
      if (!res.ok) throw new Error(await res.text());
      this.#opgeslagen = tekst;
      this.#status('Kaartje opgeslagen');
    } catch (err) {
      console.error('Kaartje:', err);
      this.#status('Kaartje niet opgeslagen. Pas de tekst aan om het nog eens te proberen.');
    }
  }

  #status(tekst) {
    const el = this.querySelector('[data-kaartje-status]');
    if (el) el.textContent = tekst;
  }
}

if (!customElements.get('smokkelaar-cadeau')) {
  customElements.define('smokkelaar-cadeau', SmokkelaarCadeau);
}
